import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '@/components/Elements/Title/Title';
import styles from './reset-password.module.scss';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { ResetPasswordForm } from '@/components/Views/ResetPassword/ResetPasswordForm/ResetPasswordForm';
import { useRequestPasswordRecoveryMutation } from '@/generated/graphql';
import { GraphQLErrorExtensions } from '@/types/GraphQLErrorExtensions';
import { Error } from '@/enums/Error';
import { HaveAnAccountLink } from '@/components/Elements/HaveAnAccountLink/HaveAnAccountLink';
import { getClientDomainType } from '@/utils/auth';
import { useAuthBranding } from '@/components/Providers/BrandingProvider/BrandingProvider';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const { branding } = useAuthBranding();
  useDocumentTitle(
    branding ? t('resetPassword.title') : t('resetPassword.pageTitle')
  );

  const [requestPasswordRecovery] = useRequestPasswordRecoveryMutation({
    onCompleted: () => {
      setSubmitting(false);
      handleMsgType({
        type: TNotification.success,
        title: t('resetPassword.success'),
        message: t('resetPassword.emailSent'),
      });
    },
    onError: ({ graphQLErrors }) => {
      setSubmitting(false);
      graphQLErrors.forEach(({ extensions }) => {
        const { code } = (extensions?.exception as GraphQLErrorExtensions)
          ?.response;
        if (code === Error.INVALID_CREDENTIALS) {
          handleMsgType({
            type: TNotification.error,
            title: t('resetPassword.failed'),
            message: t('resetPassword.invalidCredentials'),
          });
        }

        handleMsgType({
          type: TNotification.error,
          title: t('resetPassword.failed'),
          message: t('resetPassword.somethingWentWrong'),
        });
      });
    },
  });

  const [submitting, setSubmitting] = useState(false);

  const handlePasswordReset = (email: string) => {
    setSubmitting(true);
    requestPasswordRecovery({
      variables: {
        email,
        clientDomainType: getClientDomainType(),
      },
    });
  };

  return (
    <section>
      <Title title={t('resetPassword.title')} className={styles.title} />
      <ResetPasswordForm
        submitting={submitting}
        handlePasswordReset={handlePasswordReset}
      />
      <HaveAnAccountLink />
    </section>
  );
};

export default ResetPasswordPage;
